// src/cockpit4/components/ContentSeriesManager.tsx
// ============================================
// P1-功能：内容系列化管理（媒体工作室）
//
// 功能：
//   1. 内容系列/专栏管理（创建/编辑/删除，localStorage 持久化）
//   2. 系列进度追踪（计划篇数 vs 已发布篇数 vs 完成率）
//   3. 系列内容一致性保障（标题前缀/封面风格/内容结构/发布节奏 4 项内置检查）
//   4. 连载自动提醒和生成（基于发布频率 + 起始日期推算下一期，逾期预警）
//   5. 系列效果汇总分析（总阅读/总点赞/平均互动率，可选引用 useDashboardData 的 marketing 数据）
//   6. 添加内容到系列（从 RuntimeManager.query(MediaStudio, {type:"list_packages"}) 拉取的 MediaPackage 列表中选择）
//
// 数据来源：
//   - localStorage（key: xeaos_content_series）持久化用户创建的系列
//   - RuntimeManager.query(RuntimeName.MediaStudio, { type: "list_packages" }) 获取 MediaPackage 列表
//   - useDashboardData 的 marketing.platforms（可选，用于效果汇总；降级时显示空状态）
//   - 无系列时显示空状态"暂无内容系列，点击创建"，绝不展示假数据
// ============================================

import { useCallback, useEffect, useMemo, useState } from "react";
import type { ReactNode } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Layers,
  Plus,
  BookOpen,
  Clock,
  CheckCircle2,
  AlertTriangle,
  TrendingUp,
  RefreshCw,
  Loader2,
  X,
  Pencil,
  Trash2,
  Calendar,
  Target,
  Sparkles,
  ChevronRight,
  Hash,
} from "lucide-react";
import RuntimeManager from "../../runtime/RuntimeManager";
import { RuntimeName } from "../../types/runtime";
import type { MediaPackage } from "../../runtime/MediaStudio/types";
import { useDashboardData } from "../../hooks/useDashboardData";

// ============================================
// 类型与常量
// ============================================

const SERIES_STORAGE_KEY = "xeaos_content_series";

/** 发布频率 */
type Frequency = "daily" | "weekly" | "biweekly" | "monthly";

/** 系列状态 */
type SeriesStatus = "planning" | "ongoing" | "completed" | "paused";

/** 内容系列 */
interface ContentSeries {
  /** 系列 ID */
  id: string;
  /** 系列名称 */
  name: string;
  /** 系列描述 */
  description: string;
  /** 计划篇数 */
  plannedCount: number;
  /** 发布频率 */
  frequency: Frequency;
  /** 目标平台 */
  targetPlatforms: string[];
  /** 起始日期 YYYY-MM-DD */
  startDate: string;
  /** 期望标题前缀（用于一致性检查，可空） */
  titlePrefix?: string;
  /** 关联的 MediaPackage id 列表（按加入顺序） */
  contentIds: string[];
  /** 系列状态 */
  status: SeriesStatus;
  /** 创建时间 */
  createdAt: string;
}

/** 一致性检查项类型 */
type ConsistencyKey = "prefix" | "cover" | "structure" | "rhythm";

/** 一致性检查结果 */
interface ConsistencyResult {
  key: ConsistencyKey;
  label: string;
  pass: boolean;
  /** 严重程度：ok 通过 / warn 警告 / fail 不通过 */
  severity: "ok" | "warn" | "fail";
  detail: string;
}

/** 连载提醒 */
interface SerialReminder {
  /** 下一期序号（已发布 + 1） */
  nextEpisode: number;
  /** 下一期应发布日期 YYYY-MM-DD */
  nextDate: string;
  /** 距今天数（负值表示已逾期） */
  daysUntil: number;
  /** 是否逾期 */
  overdue: boolean;
}

/** 平台配置（内置） */
const PLATFORM_OPTIONS: { id: string; label: string; color: string }[] = [
  { id: "wechat", label: "公众号", color: "#10B981" },
  { id: "xiaohongshu", label: "小红书", color: "#EF4444" },
  { id: "douyin", label: "抖音", color: "#22D3EE" },
  { id: "bilibili", label: "B站", color: "#FB7299" },
  { id: "zhihu", label: "知乎", color: "#3B82F6" },
];

/** 频率 → 间隔天数 */
const FREQUENCY_DAYS: Record<Frequency, number> = {
  daily: 1,
  weekly: 7,
  biweekly: 14,
  monthly: 30,
};

const FREQUENCY_LABEL: Record<Frequency, string> = {
  daily: "每日",
  weekly: "每周",
  biweekly: "每两周",
  monthly: "每月",
};

const SERIES_STATUS_META: Record<SeriesStatus, { label: string; color: string }> = {
  planning: { label: "规划中", color: "#6B7280" },
  ongoing: { label: "进行中", color: "#34D399" },
  completed: { label: "已完成", color: "#38BDF8" },
  paused: { label: "已暂停", color: "#FBBF24" },
};

// ============================================
// 工具函数
// ============================================

/** 生成唯一 ID */
function genId(prefix: string): string {
  return `${prefix}_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
}

/** YYYY-MM-DD */
function todayStr(): string {
  return new Date().toISOString().slice(0, 10);
}

/** 日期加减天数，返回 YYYY-MM-DD */
function addDays(dateStr: string, days: number): string {
  const d = new Date(dateStr + "T00:00:00Z");
  d.setUTCDate(d.getUTCDate() + days);
  return d.toISOString().slice(0, 10);
}

/** 计算 a - b 的天数差（a、b 为 YYYY-MM-DD；正数表示 a 在 b 之后） */
function diffDays(a: string, b: string): number {
  const da = new Date(a + "T00:00:00Z").getTime();
  const db = new Date(b + "T00:00:00Z").getTime();
  return Math.round((da - db) / 86400000);
}

/** 从 MediaPackage 提取主要脚本素材（用于内容结构检查） */
function hasScript(p: MediaPackage): boolean {
  return !!(
    p.articleScript ||
    p.videoScript ||
    p.xiaohongshuScript ||
    p.bilibiliScript ||
    p.douyinScript ||
    p.liveStreamScript
  );
}

/** 从 MediaPackage 提取是否有任何封面类素材 */
function hasCover(p: MediaPackage): boolean {
  return !!(p.cover || p.wechatCover || p.douyinCover || (p.xiaohongshuGrid && p.xiaohongshuGrid.length > 0));
}

/** 提取两个字符串的最长公共前缀 */
function commonPrefix(a: string, b: string): string {
  const n = Math.min(a.length, b.length);
  let i = 0;
  while (i < n && a[i] === b[i]) i++;
  return a.slice(0, i);
}

/** 安全读取 localStorage */
function loadSeries(): ContentSeries[] {
  try {
    const raw = localStorage.getItem(SERIES_STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? (parsed as ContentSeries[]) : [];
  } catch {
    return [];
  }
}

/** 安全写入 localStorage */
function saveSeries(series: ContentSeries[]): void {
  try {
    localStorage.setItem(SERIES_STORAGE_KEY, JSON.stringify(series));
  } catch {
    // 容量超限或隐私模式：忽略
  }
}

/**
 * 一致性检查（内置 4 项规则）
 *   1. 标题前缀：所有内容 topic 共享同一前缀，或匹配系列设置的 titlePrefix
 *   2. 封面风格：所有内容都具备封面类素材
 *   3. 内容结构：所有内容都具备至少一种脚本素材
 *   4. 发布节奏：实际平均发布间隔与计划频率间隔的偏差在容差内
 */
function checkConsistency(series: ContentSeries, pkgs: MediaPackage[]): ConsistencyResult[] {
  const published = pkgs.filter((p) => p.published && p.completedAt);
  const results: ConsistencyResult[] = [];

  // 1. 标题前缀
  if (pkgs.length === 0) {
    results.push({
      key: "prefix",
      label: "标题前缀",
      pass: false,
      severity: "warn",
      detail: "暂无内容，无法校验前缀",
    });
  } else if (series.titlePrefix && series.titlePrefix.trim()) {
    const prefix = series.titlePrefix.trim();
    const mismatched = pkgs.filter((p) => !p.topic.startsWith(prefix));
    results.push({
      key: "prefix",
      label: "标题前缀",
      pass: mismatched.length === 0,
      severity: mismatched.length === 0 ? "ok" : mismatched.length >= pkgs.length / 2 ? "fail" : "warn",
      detail:
        mismatched.length === 0
          ? `全部 ${pkgs.length} 篇均以「${prefix}」开头`
          : `${mismatched.length}/${pkgs.length} 篇未使用前缀「${prefix}」`,
    });
  } else {
    // 未设置期望前缀时，自动检测是否已形成统一前缀
    const topics = pkgs.map((p) => p.topic || "");
    let prefix = topics[0] || "";
    for (const t of topics) prefix = commonPrefix(prefix, t);
    const prefixLen = prefix.trim().length;
    if (prefixLen >= 2) {
      results.push({
        key: "prefix",
        label: "标题前缀",
        pass: true,
        severity: "ok",
        detail: `自动检测到统一前缀「${prefix.trim()}」`,
      });
    } else {
      results.push({
        key: "prefix",
        label: "标题前缀",
        pass: false,
        severity: "warn",
        detail: "未检测到统一前缀，建议在系列设置中指定 titlePrefix",
      });
    }
  }

  // 2. 封面风格
  if (pkgs.length === 0) {
    results.push({ key: "cover", label: "封面风格", pass: false, severity: "warn", detail: "暂无内容" });
  } else {
    const missing = pkgs.filter((p) => !hasCover(p));
    results.push({
      key: "cover",
      label: "封面风格",
      pass: missing.length === 0,
      severity: missing.length === 0 ? "ok" : missing.length >= pkgs.length / 2 ? "fail" : "warn",
      detail:
        missing.length === 0
          ? `全部 ${pkgs.length} 篇均具备封面素材`
          : `${missing.length}/${pkgs.length} 篇缺失封面`,
    });
  }

  // 3. 内容结构
  if (pkgs.length === 0) {
    results.push({ key: "structure", label: "内容结构", pass: false, severity: "warn", detail: "暂无内容" });
  } else {
    const missing = pkgs.filter((p) => !hasScript(p));
    results.push({
      key: "structure",
      label: "内容结构",
      pass: missing.length === 0,
      severity: missing.length === 0 ? "ok" : missing.length >= pkgs.length / 2 ? "fail" : "warn",
      detail:
        missing.length === 0
          ? `全部 ${pkgs.length} 篇均包含脚本素材`
          : `${missing.length}/${pkgs.length} 篇缺失脚本`,
    });
  }

  // 4. 发布节奏
  if (published.length < 2) {
    results.push({
      key: "rhythm",
      label: "发布节奏",
      pass: false,
      severity: "warn",
      detail: published.length === 0 ? "暂无已发布内容" : "已发布不足 2 篇，无法计算节奏",
    });
  } else {
    const sorted = [...published].sort((a, b) => (a.completedAt! < b.completedAt! ? -1 : 1));
    const gaps: number[] = [];
    for (let i = 1; i < sorted.length; i++) {
      gaps.push(diffDays(sorted[i].completedAt!, sorted[i - 1].completedAt!));
    }
    const avgGap = gaps.reduce((s, x) => s + x, 0) / gaps.length;
    const plannedGap = FREQUENCY_DAYS[series.frequency];
    const deviation = Math.abs(avgGap - plannedGap);
    // 容差：计划间隔的 50%
    const tolerance = plannedGap * 0.5;
    results.push({
      key: "rhythm",
      label: "发布节奏",
      pass: deviation <= tolerance,
      severity: deviation <= tolerance ? "ok" : deviation <= plannedGap ? "warn" : "fail",
      detail: `实际平均间隔 ${avgGap.toFixed(1)} 天 vs 计划 ${plannedGap} 天（${FREQUENCY_LABEL[series.frequency]}）`,
    });
  }

  return results;
}

/**
 * 连载提醒（基于发布频率 + 起始日期 + 已发布篇数推算下一期）
 *   - 下一期序号 = 已发布篇数 + 1
 *   - 下一期日期 = startDate + (已发布篇数 * 间隔天数)
 *   - daysUntil < 0 表示逾期
 */
function computeReminder(series: ContentSeries, publishedCount: number): SerialReminder {
  const interval = FREQUENCY_DAYS[series.frequency];
  const nextEpisode = publishedCount + 1;
  const nextDate = addDays(series.startDate, publishedCount * interval);
  const daysUntil = diffDays(nextDate, todayStr());
  return {
    nextEpisode,
    nextDate,
    daysUntil,
    overdue: daysUntil < 0,
  };
}

// ============================================
// 主组件
// ============================================

export default function ContentSeriesManager() {
  // 引用 marketing 数据用于效果汇总（可选，降级时显示空状态）
  const dashboard = useDashboardData();

  const [series, setSeries] = useState<ContentSeries[]>([]);
  const [packages, setPackages] = useState<MediaPackage[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // 创建/编辑 Modal
  const [showModal, setShowModal] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState({
    name: "",
    description: "",
    plannedCount: 10,
    frequency: "weekly" as Frequency,
    targetPlatforms: ["wechat"] as string[],
    startDate: todayStr(),
    titlePrefix: "",
  });

  // 详情展开
  const [expandedId, setExpandedId] = useState<string | null>(null);

  // 添加内容到系列（弹层）
  const [addingToSeriesId, setAddingToSeriesId] = useState<string | null>(null);

  // ============================================
  // 加载 MediaPackage 列表
  // ============================================
  const loadPackages = useCallback(async () => {
    setRefreshing(true);
    setError(null);
    try {
      for (let i = 0; i < 30; i++) {
        if (RuntimeManager.isInitialized()) break;
        await new Promise((r) => setTimeout(r, 300));
      }
      if (!RuntimeManager.isInitialized() || !RuntimeManager.hasRuntime(RuntimeName.MediaStudio)) {
        setError("MediaStudio Runtime 未就绪");
        setPackages([]);
        return;
      }
      const resp = await RuntimeManager.query(RuntimeName.MediaStudio, { type: "list_packages" });
      const inner = resp?.data as { success?: boolean; data?: MediaPackage[] } | undefined;
      if (inner?.success && Array.isArray(inner.data)) {
        setPackages(inner.data as MediaPackage[]);
      } else {
        setPackages([]);
      }
    } catch (err) {
      setError((err as Error).message || "加载 MediaPackage 列表异常");
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  // 初次挂载
  useEffect(() => {
    setSeries(loadSeries());
    void loadPackages();
  }, [loadPackages]);

  // 系列变化时持久化
  useEffect(() => {
    saveSeries(series);
  }, [series]);

  // ============================================
  // 创建/编辑系列
  // ============================================
  const openCreateModal = useCallback(() => {
    setEditingId(null);
    setForm({
      name: "",
      description: "",
      plannedCount: 10,
      frequency: "weekly",
      targetPlatforms: ["wechat"],
      startDate: todayStr(),
      titlePrefix: "",
    });
    setShowModal(true);
  }, []);

  const openEditModal = useCallback((s: ContentSeries) => {
    setEditingId(s.id);
    setForm({
      name: s.name,
      description: s.description,
      plannedCount: s.plannedCount,
      frequency: s.frequency,
      targetPlatforms: s.targetPlatforms,
      startDate: s.startDate,
      titlePrefix: s.titlePrefix || "",
    });
    setShowModal(true);
  }, []);

  const submitForm = useCallback(() => {
    const name = form.name.trim();
    if (!name) return;
    if (editingId) {
      // 编辑
      setSeries((prev) =>
        prev.map((s) =>
          s.id === editingId
            ? {
                ...s,
                name,
                description: form.description.trim(),
                plannedCount: Math.max(1, Number(form.plannedCount) || 1),
                frequency: form.frequency,
                targetPlatforms: form.targetPlatforms,
                startDate: form.startDate,
                titlePrefix: form.titlePrefix.trim() || undefined,
              }
            : s
        )
      );
    } else {
      // 创建
      const item: ContentSeries = {
        id: genId("series"),
        name,
        description: form.description.trim(),
        plannedCount: Math.max(1, Number(form.plannedCount) || 1),
        frequency: form.frequency,
        targetPlatforms: form.targetPlatforms,
        startDate: form.startDate,
        titlePrefix: form.titlePrefix.trim() || undefined,
        contentIds: [],
        status: "planning",
        createdAt: todayStr(),
      };
      setSeries((prev) => [item, ...prev]);
    }
    setShowModal(false);
    setEditingId(null);
  }, [editingId, form]);

  const deleteSeries = useCallback((id: string) => {
    setSeries((prev) => prev.filter((s) => s.id !== id));
    setExpandedId((prev) => (prev === id ? null : prev));
  }, []);

  /** 把 MediaPackage 加入系列（去重，保持顺序） */
  const addContentToSeries = useCallback((seriesId: string, packageId: string) => {
    setSeries((prev) =>
      prev.map((s) => {
        if (s.id !== seriesId) return s;
        if (s.contentIds.includes(packageId)) return s;
        // 加入内容后自动转为进行中
        const nextStatus: SeriesStatus = s.status === "planning" ? "ongoing" : s.status;
        return { ...s, contentIds: [...s.contentIds, packageId], status: nextStatus };
      })
    );
  }, []);

  /** 从系列中移除内容 */
  const removeContentFromSeries = useCallback((seriesId: string, packageId: string) => {
    setSeries((prev) =>
      prev.map((s) =>
        s.id === seriesId ? { ...s, contentIds: s.contentIds.filter((id) => id !== packageId) } : s
      )
    );
  }, []);

  /** 手动更新系列状态 */
  const updateSeriesStatus = useCallback((id: string, status: SeriesStatus) => {
    setSeries((prev) => prev.map((s) => (s.id === id ? { ...s, status } : s)));
  }, []);

  // ============================================
  // 派生数据：统计 + 效果汇总
  // ============================================

  /** 全平台聚合效果（来自 useDashboardData 的 marketing 数据） */
  const marketingAgg = useMemo(() => {
    const platforms = dashboard?.marketing?.platforms;
    if (!platforms) return null;
    let views = 0;
    let likes = 0;
    let favorites = 0;
    let comments = 0;
    let shares = 0;
    let platformCount = 0;
    for (const key of Object.keys(platforms) as (keyof typeof platforms)[]) {
      const p = platforms[key];
      if (!p || p.degraded) continue;
      views += p.metrics.views || 0;
      likes += p.metrics.likes || 0;
      favorites += p.metrics.favorites || 0;
      comments += p.metrics.comments || 0;
      shares += p.metrics.shares || 0;
      platformCount += 1;
    }
    if (platformCount === 0) return null;
    const interactionRate = views > 0 ? ((likes + comments + shares) / views) * 100 : 0;
    return { views, likes, favorites, comments, shares, platformCount, interactionRate };
  }, [dashboard]);

  /** 单个系列的效果汇总（按已发布篇数比例分摊全平台聚合数据） */
  const computeSeriesEffect = useCallback(
    (s: ContentSeries) => {
      const pkgs = s.contentIds
        .map((id) => packages.find((p) => p.id === id))
        .filter((p): p is MediaPackage => !!p);
      const published = pkgs.filter((p) => p.published);
      const publishedCount = published.length;
      const totalPublishedAll = series.reduce(
        (sum, x) =>
          sum +
          x.contentIds
            .map((id) => packages.find((p) => p.id === id))
            .filter((p): p is MediaPackage => !!p && !!p.published).length,
        0
      );
      if (!marketingAgg || totalPublishedAll === 0 || publishedCount === 0) {
        return { publishedCount, views: 0, likes: 0, interactionRate: 0, hasData: false };
      }
      const ratio = publishedCount / totalPublishedAll;
      const views = Math.round(marketingAgg.views * ratio);
      const likes = Math.round(marketingAgg.likes * ratio);
      const interactionRate = marketingAgg.interactionRate;
      return { publishedCount, views, likes, interactionRate, hasData: true };
    },
    [packages, series, marketingAgg]
  );

  /** 系列统计概览 */
  const overview = useMemo(() => {
    const total = series.length;
    const ongoing = series.filter((s) => s.status === "ongoing").length;
    const completed = series.filter((s) => s.status === "completed").length;
    const completionRates = series.map((s) => {
      if (s.plannedCount <= 0) return 0;
      const pkgs = s.contentIds.map((id) => packages.find((p) => p.id === id)).filter((p): p is MediaPackage => !!p);
      const published = pkgs.filter((p) => p.published).length;
      return Math.min(100, Math.round((published / s.plannedCount) * 100));
    });
    const avgCompletion = total > 0 ? Math.round(completionRates.reduce((a, b) => a + b, 0) / total) : 0;
    return { total, ongoing, completed, avgCompletion };
  }, [series, packages]);

  // ============================================
  // 渲染
  // ============================================
  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4 }}
      className="relative rounded-xl border border-white/[0.06] bg-gradient-to-br from-[#131826]/60 to-[#0a0e1a]/60 backdrop-blur-md overflow-hidden"
    >
      {/* 顶部光带 */}
      <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-cyan-500/50 to-transparent" />

      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b border-white/[0.04]">
        <div className="flex items-center gap-2.5">
          <div className="w-7 h-7 rounded-md flex items-center justify-center bg-cyan-500/15 border border-cyan-500/30">
            <Layers size={13} className="text-cyan-400" />
          </div>
          <div>
            <h3 className="text-[13px] font-semibold text-white tracking-tight">内容系列化管理</h3>
            <p className="text-[9px] font-mono text-gray-500 uppercase tracking-wider mt-0.5">
              Content Series · {overview.total} 系列 · 进行中 {overview.ongoing} · 平均完成率 {overview.avgCompletion}%
            </p>
          </div>
        </div>
        <div className="flex items-center gap-1">
          <button
            onClick={openCreateModal}
            className="flex items-center gap-1.5 px-2.5 py-1 rounded-md bg-cyan-500/15 border border-cyan-500/30 hover:bg-cyan-500/25 transition-colors"
          >
            <Plus size={11} className="text-cyan-400" />
            <span className="text-[9px] font-mono text-cyan-300 uppercase tracking-wider">创建系列</span>
          </button>
          <button
            onClick={loadPackages}
            disabled={refreshing}
            className="flex items-center gap-1.5 px-2.5 py-1 rounded-md bg-violet-500/15 border border-violet-500/30 hover:bg-violet-500/25 transition-colors disabled:opacity-50"
          >
            {refreshing ? (
              <Loader2 size={11} className="text-violet-400 animate-spin" />
            ) : (
              <RefreshCw size={11} className="text-violet-400" />
            )}
            <span className="text-[9px] font-mono text-violet-300 uppercase tracking-wider">
              {refreshing ? "刷新中..." : "刷新内容"}
            </span>
          </button>
        </div>
      </div>

      {/* 错误提示 */}
      {error && (
        <div className="px-4 py-2 border-b border-white/[0.04]">
          <div className="px-3 py-2 rounded-md border border-amber-500/30 bg-amber-500/[0.06] text-amber-300 text-[10px] font-mono">
            {error}
          </div>
        </div>
      )}

      {loading ? (
        <div className="py-10 text-center">
          <Loader2 size={20} className="mx-auto text-cyan-400 animate-spin" />
          <p className="text-[10px] font-mono text-gray-600 mt-2">加载内容包...</p>
        </div>
      ) : (
        <>
          {/* 顶部 KPI 概览 */}
          <div className="grid grid-cols-4 gap-2 p-4 border-b border-white/[0.04]">
            <KpiCard icon={Layers} label="总系列数" value={String(overview.total)} color="#38BDF8" />
            <KpiCard icon={BookOpen} label="进行中" value={String(overview.ongoing)} color="#34D399" />
            <KpiCard icon={CheckCircle2} label="已完成" value={String(overview.completed)} color="#A78BFA" />
            <KpiCard icon={Target} label="平均完成率" value={`${overview.avgCompletion}%`} color="#FBBF24" />
          </div>

          {/* 系列卡片网格 */}
          <div className="p-4">
            {series.length === 0 ? (
              <div className="py-10 text-center">
                <Layers size={24} className="mx-auto text-gray-600 mb-2" />
                <p className="text-[11px] font-mono text-gray-500">暂无内容系列，点击创建</p>
              </div>
            ) : (
              <div className="grid grid-cols-2 gap-2.5">
                {series.map((s) => {
                  const pkgs = s.contentIds
                    .map((id) => packages.find((p) => p.id === id))
                    .filter((p): p is MediaPackage => !!p);
                  const publishedCount = pkgs.filter((p) => p.published).length;
                  const completionRate =
                    s.plannedCount > 0 ? Math.min(100, Math.round((publishedCount / s.plannedCount) * 100)) : 0;
                  const reminder = computeReminder(s, publishedCount);
                  const effect = computeSeriesEffect(s);
                  const statusMeta = SERIES_STATUS_META[s.status];
                  const isExpanded = expandedId === s.id;
                  return (
                    <motion.div
                      key={s.id}
                      layout
                      initial={{ opacity: 0, scale: 0.98 }}
                      animate={{ opacity: 1, scale: 1 }}
                      transition={{ duration: 0.2 }}
                      className="rounded-md border border-white/[0.06] bg-white/[0.02] overflow-hidden"
                    >
                      {/* 卡片头 */}
                      <div
                        className="p-2.5 cursor-pointer hover:bg-white/[0.02] transition-colors"
                        onClick={() => setExpandedId((prev) => (prev === s.id ? null : s.id))}
                      >
                        <div className="flex items-center justify-between gap-2">
                          <div className="flex items-center gap-1.5 min-w-0">
                            <span
                              className="px-1 rounded text-[8px] font-mono"
                              style={{ background: `${statusMeta.color}20`, color: statusMeta.color }}
                            >
                              {statusMeta.label}
                            </span>
                            <span className="text-[12px] text-gray-200 truncate">{s.name}</span>
                          </div>
                          <ChevronRight
                            size={12}
                            className={`text-gray-500 flex-shrink-0 transition-transform ${isExpanded ? "rotate-90" : ""}`}
                          />
                        </div>
                        {s.description && (
                          <p className="text-[9px] font-mono text-gray-500 mt-1 line-clamp-1">{s.description}</p>
                        )}
                        {/* 进度条 */}
                        <div className="mt-2">
                          <div className="flex items-center justify-between text-[9px] font-mono text-gray-400 mb-1">
                            <span>
                              {publishedCount} / {s.plannedCount} 篇
                            </span>
                            <span style={{ color: completionRate >= 100 ? "#34D399" : "#FBBF24" }}>
                              {completionRate}%
                            </span>
                          </div>
                          <div className="h-1.5 rounded bg-white/[0.04] overflow-hidden">
                            <div
                              className="h-full rounded transition-all"
                              style={{
                                width: `${completionRate}%`,
                                background:
                                  completionRate >= 100
                                    ? "linear-gradient(90deg, #34D39980, #34D399)"
                                    : "linear-gradient(90deg, #38BDF880, #38BDF8)",
                              }}
                            />
                          </div>
                        </div>
                        {/* 元信息 */}
                        <div className="flex items-center gap-2 mt-2 flex-wrap text-[9px] font-mono text-gray-500">
                          <span className="flex items-center gap-0.5">
                            <Clock size={8} />
                            {FREQUENCY_LABEL[s.frequency]}
                          </span>
                          <span>·</span>
                          <span className="flex items-center gap-0.5">
                            <Calendar size={8} />
                            {s.startDate}
                          </span>
                        </div>
                        {/* 下一期提醒 */}
                        {s.status !== "completed" && s.status !== "paused" && (
                          <div
                            className={`mt-1.5 px-1.5 py-1 rounded text-[9px] font-mono border ${
                              reminder.overdue
                                ? "border-red-500/30 bg-red-500/[0.06] text-red-300"
                                : "border-cyan-500/30 bg-cyan-500/[0.06] text-cyan-300"
                            }`}
                          >
                            {reminder.overdue ? (
                              <span className="flex items-center gap-1">
                                <AlertTriangle size={9} />
                                第 {reminder.nextEpisode} 期已逾期 {Math.abs(reminder.daysUntil)} 天（{reminder.nextDate}）
                              </span>
                            ) : (
                              <span className="flex items-center gap-1">
                                <Clock size={9} />
                                第 {reminder.nextEpisode} 期：{reminder.nextDate}（{reminder.daysUntil} 天后）
                              </span>
                            )}
                          </div>
                        )}
                      </div>

                      {/* 详情展开 */}
                      <AnimatePresence>
                        {isExpanded && (
                          <motion.div
                            initial={{ height: 0, opacity: 0 }}
                            animate={{ height: "auto", opacity: 1 }}
                            exit={{ height: 0, opacity: 0 }}
                            transition={{ duration: 0.2 }}
                            className="overflow-hidden border-t border-white/[0.04]"
                          >
                            <SeriesDetail
                              series={s}
                              packages={pkgs}
                              effect={effect}
                              onAddContent={() => setAddingToSeriesId(s.id)}
                              onRemoveContent={(pid) => removeContentFromSeries(s.id, pid)}
                              onEdit={() => openEditModal(s)}
                              onDelete={() => deleteSeries(s.id)}
                              onStatusChange={(st) => updateSeriesStatus(s.id, st)}
                            />
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </motion.div>
                  );
                })}
              </div>
            )}
          </div>
        </>
      )}

      {/* 创建/编辑系列 Modal */}
      <AnimatePresence>
        {showModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm"
            onClick={() => setShowModal(false)}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              transition={{ duration: 0.2 }}
              onClick={(e) => e.stopPropagation()}
              className="w-[420px] max-w-[90vw] rounded-xl border border-white/[0.08] bg-[#0f1320] backdrop-blur-xl overflow-hidden"
            >
              {/* 顶部光带 */}
              <div className="h-px bg-gradient-to-r from-transparent via-cyan-500/60 to-transparent" />
              <div className="flex items-center justify-between p-3 border-b border-white/[0.06]">
                <div className="flex items-center gap-2">
                  <Layers size={13} className="text-cyan-400" />
                  <span className="text-[12px] font-mono text-gray-200 uppercase tracking-wider">
                    {editingId ? "编辑系列" : "创建系列"}
                  </span>
                </div>
                <button
                  onClick={() => setShowModal(false)}
                  className="text-gray-500 hover:text-gray-300 transition-colors"
                >
                  <X size={14} />
                </button>
              </div>
              <div className="p-3 space-y-2.5 max-h-[70vh] overflow-y-auto">
                <FormField label="系列名称 *">
                  <input
                    value={form.name}
                    onChange={(e) => setForm((s) => ({ ...s, name: e.target.value }))}
                    placeholder="例如：创业者认知系列"
                    className="form-input"
                  />
                </FormField>
                <FormField label="系列描述">
                  <textarea
                    value={form.description}
                    onChange={(e) => setForm((s) => ({ ...s, description: e.target.value }))}
                    placeholder="一句话描述系列主题与受众"
                    rows={2}
                    className="form-input resize-none"
                  />
                </FormField>
                <div className="grid grid-cols-2 gap-2">
                  <FormField label="计划篇数">
                    <input
                      type="number"
                      min={1}
                      value={form.plannedCount}
                      onChange={(e) => setForm((s) => ({ ...s, plannedCount: Number(e.target.value) || 1 }))}
                      className="form-input"
                    />
                  </FormField>
                  <FormField label="发布频率">
                    <select
                      value={form.frequency}
                      onChange={(e) => setForm((s) => ({ ...s, frequency: e.target.value as Frequency }))}
                      className="form-input"
                    >
                      <option value="daily">每日</option>
                      <option value="weekly">每周</option>
                      <option value="biweekly">每两周</option>
                      <option value="monthly">每月</option>
                    </select>
                  </FormField>
                </div>
                <FormField label="起始日期">
                  <input
                    type="date"
                    value={form.startDate}
                    onChange={(e) => setForm((s) => ({ ...s, startDate: e.target.value }))}
                    className="form-input"
                  />
                </FormField>
                <FormField label="目标平台">
                  <div className="flex items-center gap-1 flex-wrap">
                    {PLATFORM_OPTIONS.map((p) => {
                      const selected = form.targetPlatforms.includes(p.id);
                      return (
                        <button
                          key={p.id}
                          type="button"
                          onClick={() =>
                            setForm((s) => ({
                              ...s,
                              targetPlatforms: selected
                                ? s.targetPlatforms.filter((x) => x !== p.id)
                                : [...s.targetPlatforms, p.id],
                            }))
                          }
                          className={`px-1.5 py-0.5 rounded text-[9px] font-mono border transition-colors ${
                            selected
                              ? "text-white"
                              : "text-gray-500 border-white/[0.06] hover:text-gray-300"
                          }`}
                          style={
                            selected
                              ? { background: `${p.color}20`, borderColor: `${p.color}40`, color: p.color }
                              : undefined
                          }
                        >
                          {p.label}
                        </button>
                      );
                    })}
                  </div>
                </FormField>
                <FormField label="期望标题前缀（可选，用于一致性检查）">
                  <input
                    value={form.titlePrefix}
                    onChange={(e) => setForm((s) => ({ ...s, titlePrefix: e.target.value }))}
                    placeholder="例如：【创业者认知】"
                    className="form-input"
                  />
                </FormField>
              </div>
              <div className="flex items-center justify-end gap-2 p-3 border-t border-white/[0.06]">
                <button
                  onClick={() => setShowModal(false)}
                  className="px-2.5 py-1 rounded text-[10px] font-mono text-gray-400 hover:text-gray-200 transition-colors"
                >
                  取消
                </button>
                <button
                  onClick={submitForm}
                  disabled={!form.name.trim()}
                  className="flex items-center gap-1 px-2.5 py-1 rounded text-[10px] font-mono uppercase tracking-wider bg-cyan-500/15 border border-cyan-500/30 text-cyan-300 hover:bg-cyan-500/25 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  <Plus size={11} />
                  {editingId ? "保存修改" : "创建"}
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* 添加内容到系列 Modal */}
      <AnimatePresence>
        {addingToSeriesId && (
          <AddContentModal
            series={series.find((s) => s.id === addingToSeriesId)!}
            packages={packages}
            onClose={() => setAddingToSeriesId(null)}
            onAdd={(pid) => addContentToSeries(addingToSeriesId, pid)}
          />
        )}
      </AnimatePresence>

      {/* 内联样式（form-input 类） */}
      <style>{`
        .form-input {
          width: 100%;
          padding: 6px 8px;
          border-radius: 6px;
          background: rgba(255,255,255,0.03);
          border: 1px solid rgba(255,255,255,0.06);
          font-size: 11px;
          font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
          color: #e5e7eb;
          outline: none;
          transition: border-color 0.15s;
        }
        .form-input:focus { border-color: rgba(34,211,238,0.4); }
        .form-input::placeholder { color: #4b5563; }
      `}</style>
    </motion.div>
  );
}

// ============================================
// 系列详情面板
// ============================================
interface SeriesDetailProps {
  series: ContentSeries;
  packages: MediaPackage[];
  effect: {
    publishedCount: number;
    views: number;
    likes: number;
    interactionRate: number;
    hasData: boolean;
  };
  onAddContent: () => void;
  onRemoveContent: (packageId: string) => void;
  onEdit: () => void;
  onDelete: () => void;
  onStatusChange: (status: SeriesStatus) => void;
}

function SeriesDetail({
  series,
  packages,
  effect,
  onAddContent,
  onRemoveContent,
  onEdit,
  onDelete,
  onStatusChange,
}: SeriesDetailProps) {
  const publishedPkgs = packages.filter((p) => p.published);
  const pendingPkgs = packages.filter((p) => !p.published);
  const consistency = useMemo(() => checkConsistency(series, packages), [series, packages]);
  const reminder = computeReminder(series, publishedPkgs.length);

  return (
    <div className="p-3 space-y-3 bg-white/[0.01]">
      {/* 操作栏 */}
      <div className="flex items-center justify-between flex-wrap gap-1">
        <div className="flex items-center gap-1">
          {(["planning", "ongoing", "paused", "completed"] as SeriesStatus[]).map((st) => {
            const meta = SERIES_STATUS_META[st];
            const active = series.status === st;
            return (
              <button
                key={st}
                onClick={() => onStatusChange(st)}
                className={`px-1.5 py-0.5 rounded text-[8px] font-mono border transition-colors ${
                  active ? "text-white" : "text-gray-500 border-white/[0.06] hover:text-gray-300"
                }`}
                style={active ? { background: `${meta.color}20`, borderColor: `${meta.color}40`, color: meta.color } : undefined}
              >
                {meta.label}
              </button>
            );
          })}
        </div>
        <div className="flex items-center gap-1">
          <button
            onClick={onEdit}
            className="flex items-center gap-1 px-1.5 py-0.5 rounded text-[8px] font-mono text-gray-400 hover:text-cyan-300 border border-white/[0.06] hover:border-cyan-500/30 transition-colors"
          >
            <Pencil size={9} />
            编辑
          </button>
          <button
            onClick={onDelete}
            className="flex items-center gap-1 px-1.5 py-0.5 rounded text-[8px] font-mono text-gray-400 hover:text-red-300 border border-white/[0.06] hover:border-red-500/30 transition-colors"
          >
            <Trash2 size={9} />
            删除
          </button>
        </div>
      </div>

      {/* 连载提醒 */}
      <Section icon={Clock} title="连载提醒" color="#38BDF8">
        {series.status === "completed" ? (
          <p className="text-[10px] font-mono text-gray-500">系列已完成，无待发布提醒</p>
        ) : series.status === "paused" ? (
          <p className="text-[10px] font-mono text-gray-500">系列已暂停</p>
        ) : reminder.overdue ? (
          <div className="flex items-start gap-1.5 text-[10px] font-mono text-red-300">
            <AlertTriangle size={11} className="flex-shrink-0 mt-0.5" />
            <span>
              第 {reminder.nextEpisode} 期应于 {reminder.nextDate} 发布，已逾期 {Math.abs(reminder.daysUntil)} 天。
              建议尽快发布或调整频率。
            </span>
          </div>
        ) : (
          <div className="flex items-start gap-1.5 text-[10px] font-mono text-cyan-300">
            <Clock size={11} className="flex-shrink-0 mt-0.5" />
            <span>
              第 {reminder.nextEpisode} 期预计 {reminder.nextDate} 发布（{reminder.daysUntil} 天后），频率：
              {FREQUENCY_LABEL[series.frequency]}。
            </span>
          </div>
        )}
      </Section>

      {/* 一致性检查 */}
      <Section icon={CheckCircle2} title="一致性检查" color="#A78BFA">
        <div className="grid grid-cols-2 gap-1.5">
          {consistency.map((c) => (
            <div
              key={c.key}
              className="rounded border px-1.5 py-1"
              style={{
                borderColor:
                  c.severity === "ok"
                    ? "rgba(52,211,153,0.3)"
                    : c.severity === "warn"
                    ? "rgba(251,191,36,0.3)"
                    : "rgba(239,68,68,0.3)",
                background:
                  c.severity === "ok"
                    ? "rgba(52,211,153,0.05)"
                    : c.severity === "warn"
                    ? "rgba(251,191,36,0.05)"
                    : "rgba(239,68,68,0.05)",
              }}
            >
              <div className="flex items-center gap-1">
                {c.severity === "ok" ? (
                  <CheckCircle2 size={9} className="text-emerald-400" />
                ) : (
                  <AlertTriangle
                    size={9}
                    className={c.severity === "warn" ? "text-amber-400" : "text-red-400"}
                  />
                )}
                <span className="text-[9px] font-mono text-gray-300">{c.label}</span>
              </div>
              <p className="text-[8px] font-mono text-gray-500 mt-0.5 leading-tight">{c.detail}</p>
            </div>
          ))}
        </div>
      </Section>

      {/* 效果汇总 */}
      <Section icon={TrendingUp} title="效果汇总" color="#34D399">
        {effect.hasData ? (
          <div className="grid grid-cols-4 gap-1.5">
            <MiniStat label="已发布" value={String(effect.publishedCount)} color="#34D399" />
            <MiniStat label="总阅读" value={String(effect.views)} color="#38BDF8" />
            <MiniStat label="总点赞" value={String(effect.likes)} color="#FBBF24" />
            <MiniStat label="互动率" value={`${effect.interactionRate.toFixed(1)}%`} color="#A78BFA" />
          </div>
        ) : (
          <p className="text-[10px] font-mono text-gray-500">
            {effect.publishedCount === 0
              ? "暂无已发布内容，发布后展示效果"
              : "marketing 数据未就绪，暂无法统计效果"}
          </p>
        )}
      </Section>

      {/* 内容列表 */}
      <Section
        icon={BookOpen}
        title={`内容列表（${packages.length}）`}
        color="#38BDF8"
        action={
          <button
            onClick={onAddContent}
            className="flex items-center gap-1 px-1.5 py-0.5 rounded text-[8px] font-mono text-cyan-300 border border-cyan-500/30 bg-cyan-500/10 hover:bg-cyan-500/20 transition-colors"
          >
            <Plus size={9} />
            添加内容
          </button>
        }
      >
        {packages.length === 0 ? (
          <p className="text-[10px] font-mono text-gray-500 text-center py-2">暂未关联内容，点击「添加内容」</p>
        ) : (
          <div className="space-y-1">
            {publishedPkgs.length > 0 && (
              <div className="text-[8px] font-mono text-gray-500 uppercase tracking-wider mt-0.5">
                已发布（{publishedPkgs.length}）
              </div>
            )}
            {publishedPkgs.map((p, idx) => (
              <ContentRow
                key={p.id}
                index={idx + 1}
                pkg={p}
                published
                onRemove={() => onRemoveContent(p.id)}
              />
            ))}
            {pendingPkgs.length > 0 && (
              <div className="text-[8px] font-mono text-gray-500 uppercase tracking-wider mt-1.5">
                待发布（{pendingPkgs.length}）
              </div>
            )}
            {pendingPkgs.map((p, idx) => (
              <ContentRow
                key={p.id}
                index={publishedPkgs.length + idx + 1}
                pkg={p}
                published={false}
                onRemove={() => onRemoveContent(p.id)}
              />
            ))}
          </div>
        )}
      </Section>
    </div>
  );
}

// ============================================
// 内容行
// ============================================
interface ContentRowProps {
  index: number;
  pkg: MediaPackage;
  published: boolean;
  onRemove: () => void;
}

function ContentRow({ index, pkg, published, onRemove }: ContentRowProps) {
  return (
    <div className="flex items-center gap-1.5 px-1.5 py-1 rounded border border-white/[0.04] bg-white/[0.02]">
      <span className="text-[9px] font-mono text-gray-500 w-5 text-right flex-shrink-0">#{index}</span>
      {published ? (
        <CheckCircle2 size={10} className="text-emerald-400 flex-shrink-0" />
      ) : (
        <Clock size={10} className="text-amber-400 flex-shrink-0" />
      )}
      <span className="text-[10px] font-mono text-gray-300 truncate flex-1">{pkg.topic || "未命名"}</span>
      {pkg.completedAt && (
        <span className="text-[8px] font-mono text-gray-500 flex-shrink-0">{pkg.completedAt.slice(0, 10)}</span>
      )}
      <button
        onClick={onRemove}
        className="text-gray-600 hover:text-red-400 transition-colors flex-shrink-0"
        title="从系列移除"
      >
        <X size={10} />
      </button>
    </div>
  );
}

// ============================================
// 添加内容 Modal
// ============================================
interface AddContentModalProps {
  series: ContentSeries;
  packages: MediaPackage[];
  onClose: () => void;
  onAdd: (packageId: string) => void;
}

function AddContentModal({ series, packages, onClose, onAdd }: AddContentModalProps) {
  const [keyword, setKeyword] = useState("");
  const filtered = useMemo(() => {
    const kw = keyword.trim().toLowerCase();
    if (!kw) return packages;
    return packages.filter((p) => (p.topic || "").toLowerCase().includes(kw));
  }, [packages, keyword]);

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.2 }}
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm"
      onClick={onClose}
    >
      <motion.div
        initial={{ scale: 0.95, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.95, opacity: 0 }}
        transition={{ duration: 0.2 }}
        onClick={(e) => e.stopPropagation()}
        className="w-[460px] max-w-[90vw] rounded-xl border border-white/[0.08] bg-[#0f1320] backdrop-blur-xl overflow-hidden"
      >
        <div className="h-px bg-gradient-to-r from-transparent via-cyan-500/60 to-transparent" />
        <div className="flex items-center justify-between p-3 border-b border-white/[0.06]">
          <div className="flex items-center gap-2 min-w-0">
            <Sparkles size={13} className="text-cyan-400 flex-shrink-0" />
            <span className="text-[12px] font-mono text-gray-200 uppercase tracking-wider truncate">
              添加内容到「{series.name}」
            </span>
          </div>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-300 transition-colors flex-shrink-0">
            <X size={14} />
          </button>
        </div>
        <div className="p-3 border-b border-white/[0.06]">
          <input
            value={keyword}
            onChange={(e) => setKeyword(e.target.value)}
            placeholder="按主题关键字筛选"
            className="form-input"
          />
        </div>
        <div className="p-3 max-h-[50vh] overflow-y-auto">
          {packages.length === 0 ? (
            <p className="text-[10px] font-mono text-gray-500 text-center py-4">
              暂无可选 MediaPackage，请先在媒体工作室生成内容
            </p>
          ) : filtered.length === 0 ? (
            <p className="text-[10px] font-mono text-gray-500 text-center py-4">无匹配内容</p>
          ) : (
            <div className="space-y-1">
              {filtered.map((p) => {
                const already = series.contentIds.includes(p.id);
                return (
                  <div
                    key={p.id}
                    className="flex items-center gap-1.5 px-1.5 py-1 rounded border border-white/[0.04] bg-white/[0.02]"
                  >
                    <Hash size={9} className="text-gray-500 flex-shrink-0" />
                    <span className="text-[10px] font-mono text-gray-300 truncate flex-1">{p.topic || "未命名"}</span>
                    <span
                      className="text-[8px] font-mono flex-shrink-0"
                      style={{ color: p.published ? "#34D399" : "#FBBF24" }}
                    >
                      {p.published ? "已发布" : "待发布"}
                    </span>
                    <button
                      onClick={() => onAdd(p.id)}
                      disabled={already}
                      className="px-1.5 py-0.5 rounded text-[8px] font-mono border border-cyan-500/30 text-cyan-300 hover:bg-cyan-500/20 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                    >
                      {already ? "已加入" : "加入"}
                    </button>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </motion.div>
    </motion.div>
  );
}

// ============================================
// KPI 小卡片
// ============================================
interface KpiCardProps {
  icon: typeof Target;
  label: string;
  value: string;
  color: string;
}

function KpiCard({ icon: Icon, label, value, color }: KpiCardProps) {
  return (
    <div
      className="rounded-md border border-white/[0.06] bg-white/[0.02] px-2.5 py-1.5 flex items-center gap-2"
      style={{ borderLeft: `2px solid ${color}` }}
    >
      <Icon size={13} style={{ color }} className="flex-shrink-0" />
      <div className="min-w-0">
        <div className="text-[9px] font-mono text-gray-500 uppercase tracking-wider truncate">{label}</div>
        <div className="text-[13px] font-mono font-semibold truncate" style={{ color }}>
          {value}
        </div>
      </div>
    </div>
  );
}

// ============================================
// 分区标题
// ============================================
interface SectionProps {
  icon: typeof Target;
  title: string;
  color: string;
  action?: ReactNode;
  children: ReactNode;
}

function Section({ icon: Icon, title, color, action, children }: SectionProps) {
  return (
    <div>
      <div className="flex items-center justify-between mb-1.5">
        <div className="flex items-center gap-1.5">
          <Icon size={11} style={{ color }} />
          <span className="text-[9px] font-mono text-gray-400 uppercase tracking-wider">{title}</span>
        </div>
        {action}
      </div>
      {children}
    </div>
  );
}

// ============================================
// 小型统计指标
// ============================================
function MiniStat({ label, value, color }: { label: string; value: string; color: string }) {
  return (
    <div
      className="rounded border border-white/[0.04] bg-white/[0.02] px-1.5 py-1 text-center"
      style={{ borderTop: `2px solid ${color}` }}
    >
      <div className="text-[8px] font-mono text-gray-500 uppercase tracking-wider truncate">{label}</div>
      <div className="text-[11px] font-mono font-bold mt-0.5" style={{ color }}>
        {value}
      </div>
    </div>
  );
}

// ============================================
// 表单字段包装
// ============================================
function FormField({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div>
      <label className="block text-[9px] font-mono text-gray-500 uppercase tracking-wider mb-1">{label}</label>
      {children}
    </div>
  );
}
